import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import SectionLabel from "../components/SectionLabel";
import { MEDIA, STATS, VALUES } from "../lib/constants";
import { ease } from "../lib/motion";

const PageHero = () => (
  <section className="relative pt-44 pb-20 md:pt-52 md:pb-32">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease }}
        className="md:col-span-7"
      >
        <p className="overline mb-6">About us</p>
        <h1
          className="font-display tracking-tighter leading-[0.95] text-white"
          style={{ fontSize: "clamp(44px, 7vw, 112px)", fontWeight: 600 }}
          data-testid="about-title"
        >
          We are a small studio, building software that{" "}
          <span className="text-[#d4af37] italic">earns its place</span>.
        </h1>
        <p className="mt-8 text-lg text-[#a0abc0] max-w-xl leading-relaxed">
          Forty-two engineers, designers and strategists across three offices.
          Picky about the work we take on, devoted to the teams we choose.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease }}
        className="md:col-span-5"
      >
        <div className="relative h-[420px] md:h-[520px] rounded-2xl overflow-hidden border border-white/10">
          <img
            src={MEDIA.aboutOffice}
            alt="Evo Technology studio"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050814]/60 to-transparent" />
          <div className="absolute bottom-6 left-6 text-white">
            <p className="font-mono text-xs text-[#d4af37] tracking-widest">
              EVO HQ
            </p>
            <p className="font-display text-lg mt-1">
              San Francisco — Berlin — Bengaluru
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const Story = () => (
  <section className="py-24 md:py-32 bg-[#050814] border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        <SectionLabel number="01" label="Our story" />
      </div>
      <div className="md:col-span-8 space-y-8 text-[#a0abc0] text-lg leading-relaxed">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
        >
          Evo Technology was founded in <span className="text-white">2017</span>{" "}
          by three former staff engineers who had spent a decade watching big
          consultancies churn out interchangeable software for fees that bore
          no relationship to the value delivered. We thought there was a
          quieter, more honest way to do this work.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1, ease }}
        >
          We started with two clients and a single conviction: that hiring
          senior people, paying them properly, and refusing to scale beyond
          what we could personally vouch for would produce better software and
          better businesses. Eight years later we're a team of 42 across three
          continents, and our founding clients are still with us.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2, ease }}
        >
          We've shipped products for fintech unicorns, healthcare networks,
          city governments and small ambitious startups. The throughline isn't
          industry or stack — it's the kind of people we work with. People who
          care, in detail, about what they build.
        </motion.p>
      </div>
    </div>
  </section>
);

const Values = () => (
  <section className="py-24 md:py-32 bg-[#050814] border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionLabel number="02" label="Principles" />
      <h2 className="font-display text-3xl md:text-5xl tracking-tight text-white max-w-3xl mb-16 leading-[1.05]">
        Four ideas we keep coming back to.
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {VALUES.map((v, i) => (
          <motion.div
            key={v.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.08, ease }}
            className="rounded-2xl border border-white/5 bg-[#0a0f1d] p-10 hover-lift hover:border-[#d4af37]/30"
            data-testid={`value-${i}`}
          >
            <p className="font-mono text-xs text-[#d4af37] tracking-widest mb-6">
              0{i + 1}
            </p>
            <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight mb-4">
              {v.title}
            </h3>
            <p className="text-[#a0abc0] leading-relaxed">{v.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="py-24 md:py-32 bg-[#050814] border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionLabel number="03" label="By the numbers" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-8 mt-12">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.08, ease }}
          >
            <p
              className="font-display text-white tracking-tighter leading-none"
              style={{ fontSize: "clamp(48px, 7vw, 112px)", fontWeight: 600 }}
            >
              {s.value}
            </p>
            <p className="overline mt-4">{s.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Team = () => (
  <section className="py-24 md:py-32 bg-[#050814] border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
      <div className="md:col-span-7">
        <SectionLabel number="04" label="The team" />
        <h2 className="font-display text-3xl md:text-5xl tracking-tight text-white leading-[1.05] max-w-xl">
          Senior people. No hand-offs to juniors mid-engagement.
        </h2>
        <p className="mt-6 text-[#a0abc0] max-w-lg leading-relaxed">
          Every engagement is led by a partner who codes, designs or both.
          You'll know who is doing the work, and you'll talk to them directly.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-3 mt-10 bg-[#d4af37] hover:bg-[#f2e3c6] text-[#050814] font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:gap-5"
        >
          Work with us <ArrowRight size={18} weight="bold" />
        </Link>
      </div>
      <div className="md:col-span-5">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease }}
          className="relative h-[440px] rounded-2xl overflow-hidden border border-white/10 grayscale hover:grayscale-0 transition-all duration-700"
        >
          <img
            src={MEDIA.teamMeeting}
            alt="Evo team meeting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050814]/70 via-transparent to-transparent" />
        </motion.div>
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero />
      <Story />
      <Values />
      <Stats />
      <Team />
    </div>
  );
}
